import { styled } from '@mui/material/styles';
import Button from 'src/presentations/components/atom/button';
import Modal from 'src/presentations/components/molecules/modal';
import { useCallback, useState } from 'react';
import { openToast } from 'src/use-cases/toast/useToast';
import { compressToURI } from 'lz-ts';
import { stringify } from 'superjson';
import { useSearchParams } from 'react-router-dom';
import { TextField } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import CreateIcon from 'src/assets/images/icons/create-report.svg';
import UpdateReport from '../../atom/icons/UpdateReport';
import { useSaveReport, useUpdateReport } from '../analytics/useAnalytics';

interface IModalProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  dataFilter: Record<string, any>;
}

const StyledModal = styled(Modal)({
  zIndex: 9999,

  '& .MuiDialog-paper': {
    maxWidth: '540px',
    borderRadius: '16px',
  },
});

enum SaveType {
  UPDATE,
  CREATE,
}

// Update Report Option Component
interface IUpdateProps {
  onUpdate: () => void;
  isChosen: boolean;
  disabled: boolean;
}

const UpdateReportOption = ({ onUpdate, isChosen, disabled }: IUpdateProps) => (
  <button
    type='button'
    disabled={disabled}
    onClick={onUpdate}
    className={`p-[20px] items-center gap-[20px] flex border-[1px] ${isChosen ? 'border-[#00992B] shadow-md' : 'border-[#DFDFDF]'} rounded-[20px] w-full`}
  >
    <UpdateReport color={disabled ? '#808080' : '#00992B'} />
    <div className={`text-left ${disabled ? 'text-[#808080]' : ''}`}>
      <h1 className='font-extrabold'>Replace original report</h1>
      <p className='text-[#808080]'>You will save the changes replacing this report</p>
    </div>
  </button>
);

// Create Report Option Component
interface ICreateProps {
  onCreate: () => void;
  isChosen: boolean;
}

const CreateReportOption = ({ onCreate, isChosen }: ICreateProps) => {
  const { register } = useFormContext();
  return (
    <button
      type='button'
      onClick={onCreate}
      className={`p-[20px] border-[1px] ${isChosen ? 'border-[#00992B] shadow-md' : 'border-[#DFDFDF]'} rounded-[20px] w-full text-left`}
    >
      <div className='flex gap-[20px] items-center'>
        <img src={CreateIcon} alt='CreateIcon' />
        <div className='text-left'>
          <h1 className='font-extrabold'>Save as new report</h1>
          <p className='text-[#808080]'>You will save the update as a new report</p>
        </div>
      </div>

      {isChosen && (
        <div className='mt-[24px] flex flex-col gap-[16px]'>
          <TextField className='w-full' label='New Report Name' size='small' {...register('name')} />
          <TextField className='w-full' label='Description' size='small' {...register('description')} multiline rows={3} />
        </div>
      )}
    </button>
  );
};

export const SavePerformanceReportModal: React.FC<IModalProps> = (props) => {
  const { open, title = 'Save Performance Report', onClose, dataFilter } = props;
  const [saveType, setSaveType] = useState<SaveType>(null);
  const { mutateAsync: createReport } = useSaveReport();
  const { mutateAsync: updateReport } = useUpdateReport();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const { watch } = useFormContext();

  // Get form values directly from the form context
  const { name = '', description = '', ...formValues } = watch() || {};

  const onCloseModal = useCallback(() => {
    setSaveType(null);
    onClose();
  }, [onClose]);

  const saveReport = useCallback(() => {
    if (!name || !name.trim()) {
      openToast({
        toastType: 'error',
        title: 'Error',
        description: 'Report name is required',
      });
      return;
    }

    const currentReport = {
      name,
      description,
      filters: {
        selectedChartView: dataFilter?.selectedChartView || 'TREND',
        formValues: formValues || {},
      },
    };

    try {
      if (saveType === SaveType.CREATE || !id) {
        createReport({
          name,
          description,
          hashedFilters: compressToURI(stringify(currentReport)),
          type: 'PERFORMANCE_ASSESSMENT' as const,
        });
        openToast({
          toastType: 'success',
          title: 'Success',
          description: `${name} successfully saved`,
        });
      } else {
        updateReport({
          id,
          name,
          description,
          hashedFilters: compressToURI(stringify(currentReport)),
          type: 'PERFORMANCE_ASSESSMENT' as const,
        });
        openToast({
          toastType: 'success',
          title: 'Success',
          description: `${name} successfully updated`,
        });
      }
      onCloseModal();
    } catch (error) {
      openToast({
        toastType: 'error',
        title: 'Error',
        description: 'Failed to save report',
      });
    }
  }, [createReport, updateReport, name, description, formValues, dataFilter, saveType, id, onCloseModal]);

  return (
    <StyledModal open={open} title={title} fullWidth onClose={onCloseModal}>
      <div className='relative'>
        <div className='flex flex-col gap-[24px]'>
          <UpdateReportOption disabled={!id} onUpdate={() => setSaveType(SaveType.UPDATE)} isChosen={saveType === SaveType.UPDATE} />
          <CreateReportOption onCreate={() => setSaveType(SaveType.CREATE)} isChosen={saveType === SaveType.CREATE} />
        </div>

        <div className='mt-48 flex gap-[12px]'>
          <Button className='basis-1/2' variant='primary_outline' onClick={onCloseModal}>
            Cancel
          </Button>
          <Button className='basis-1/2' variant='primary' onClick={saveReport} disabled={saveType === null}>
            Save
          </Button>
        </div>
      </div>
    </StyledModal>
  );
};
